<script setup lang="ts">
  import { computed } from "vue";
  import { storeToRefs } from "pinia";
  import { ChartColumn, Globe, Lock } from "lucide-vue-next";

  import {
    IndexTable,
    IndexTableName,
    IndexTableDate,
    IndexTableEmpty,
    IndexTableProgress,
  } from "@/components/episto-ui/index-table";
  import type { DataTableColumn } from "@/components/episto-ui/data-table";
  import { ActionsDropdown } from "@/components/episto-ui/actions-dropdown";
  import SurveyStatusBadge from "@/components/Survey/SurveyStatusBadge.vue";
  import { Badge } from "@/components/ui/badge";
  import { Button } from "@/components/ui/button";
  import { Tooltip } from "@/components/ui/tooltip";
  import { useSurveysStore } from "@/stores/surveys";
  import { useCurrentUserStore } from "@/stores/currentUser";
  import { useRowNavigation } from "@/composables/useRowNavigation";
  import { access } from "@/utils/constants/userRoles";
  import { useSurveyTargets } from "@/utils/composables/surveyCard";
  import { useFlagEmojis } from "@/utils/composables/flagEmojis";
  import { formatCount } from "@/utils/formatters";
  import { POPULATION_AUTO_COLORS } from "@/lib/chartPalettes";
  import type { AccessKey, SurveyListItem } from "@/types";

  defineProps<{
    surveys: SurveyListItem[];
    surveysCount: number;
    currentPage: number;
    loading: boolean;
  }>();

  const surveysStore = useSurveysStore();
  const { actionDropdownSurveys } = storeToRefs(surveysStore);
  const { isAdmin, userAccountIds } = storeToRefs(useCurrentUserStore());
  const { openRow } = useRowNavigation();

  const showAccountName = computed(() => isAdmin.value || (userAccountIds.value?.length ?? 0) > 1);

  const { isMultiCountry, firstTargetCountry, determineCountryInfo } = useSurveyTargets();
  const determineFlag = useFlagEmojis();

  const columns: DataTableColumn<SurveyListItem>[] = [
    { key: "name", label: "Study", headerClass: "[&>div]:pl-8" },
    { key: "status", label: "Status", width: 100 },
    { key: "completes", label: "Completes", width: 260 },
    { key: "loi", label: "LOI", width: 80 },
    { key: "date", label: "Date", width: 100 },
  ];

  const hasAccess = (survey: SurveyListItem, key: string) => (survey.accesses ?? []).includes(key as AccessKey);
  const isReadOnly = (survey: SurveyListItem) => survey.freemium_demo || !hasAccess(survey, access.SURVEYS_WRITE);
  const canAnalyze = (survey: SurveyListItem) => hasAccess(survey, access.SURVEYS_ANALYZE);
  const surveyLink = (survey: SurveyListItem) =>
    isReadOnly(survey) && !canAnalyze(survey)
      ? `/surveys/${survey.token}/progress`
      : `/surveys/${survey.token}/questions#${isReadOnly(survey) ? "review" : "edit"}`;

  const progress = (survey: SurveyListItem) => ({
    done: survey.progress?.total?.n_current ?? 0,
    goal: survey.progress?.total?.n_target ?? 0,
  });
  const barProps = (survey: SurveyListItem) =>
    survey.status === "live" ? { barClass: "bg-episto-teal" } : { barColor: POPULATION_AUTO_COLORS[0] };

  const loi = (survey: SurveyListItem) =>
    survey.median_loi != null ? `${Math.round(survey.median_loi / 60)} min` : null;

  const hasResults = (survey: SurveyListItem) => survey.status !== "draft" || progress(survey).done > 0;
</script>

<template>
  <IndexTable
    :items="surveys"
    :columns="columns"
    :loading="loading"
    :current-page="currentPage"
    :total="surveysCount"
    row-data-cy="dashboard-card"
    actions-class="w-[130px]"
    item-key="token"
    @row-click="(survey, event) => openRow(surveyLink(survey), event)"
    @page-change="surveysStore.changePage({ currentPage: $event })">
    <template #cell-name="{ item }">
      <div :class="{ 'opacity-60': item.freemium_demo }">
        <IndexTableName
          :name="item.name"
          :subtitle="showAccountName ? item.account_name : null"
          name-data-cy="info-name">
          <template #leading>
            <Lock
              v-if="item.freemium_demo"
              class="size-4 text-muted-foreground"
              data-cy="info-lock" />
            <Tooltip v-else-if="isMultiCountry(item.targets)">
              <span class="flex size-5 items-center justify-center text-muted-foreground">
                <Globe class="size-4" />
              </span>
              <template #content>
                <span
                  v-for="(label, idx) in determineCountryInfo(item.targets)"
                  :key="idx">
                  {{ label }}<br />
                </span>
              </template>
            </Tooltip>
            <Tooltip
              v-else
              :content="determineCountryInfo(item.targets).join(', ')">
              <span
                class="flex size-5 items-center justify-center text-sm leading-none"
                data-cy="info-flag">
                {{ determineFlag(firstTargetCountry(item.targets)) }}
              </span>
            </Tooltip>
          </template>
          <Badge
            v-if="item.ai_chat"
            variant="purple"
            class="shrink-0 text-3xs uppercase tracking-[0.06em]"
            data-cy="info-study-type">
            Quali
          </Badge>
        </IndexTableName>
      </div>
    </template>

    <template #cell-status="{ item }">
      <SurveyStatusBadge
        :status="item.status"
        class="-ml-2.5"
        data-cy="info-status" />
    </template>

    <template #cell-completes="{ item }">
      <RouterLink
        :to="`/surveys/${item.token}/progress`"
        class="block rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
        data-cy="progress-btn"
        @click.stop>
        <IndexTableProgress
          :value="progress(item).done"
          :total="progress(item).goal"
          v-bind="barProps(item)">
          <span
            :class="progress(item).done ? 'font-semibold text-primary' : 'text-foreground-subtle'"
            data-cy="info-n-current"
            >{{ formatCount(progress(item).done) }}</span
          ><span
            class="text-foreground-subtle"
            data-cy="info-n-target">
            / {{ formatCount(progress(item).goal) }}</span
          >
        </IndexTableProgress>
      </RouterLink>
    </template>

    <template #cell-loi="{ item }">
      <span
        v-if="loi(item)"
        class="text-muted-foreground"
        data-cy="info-median-loi">
        {{ loi(item) }}
      </span>
      <IndexTableEmpty
        v-else
        data-cy="info-median-loi" />
    </template>

    <template #cell-date="{ item }">
      <IndexTableDate
        :date="item.created_at"
        data-cy="info-created-at" />
    </template>

    <template #actions="{ item }">
      <div
        class="flex items-center justify-end gap-1"
        @click.stop>
        <RouterLink
          v-if="canAnalyze(item)"
          :to="`/surveys/${item.token}/dashboards`"
          :tabindex="hasResults(item) ? undefined : -1"
          :class="!hasResults(item) && 'pointer-events-none'">
          <Button
            variant="ghost"
            size="sm"
            class="text-primary hover:bg-background hover:shadow-xs"
            :disabled="!hasResults(item)"
            data-cy="results-btn">
            <ChartColumn />
            Results
          </Button>
        </RouterLink>
        <ActionsDropdown
          v-if="!isReadOnly(item)"
          :actions="actionDropdownSurveys"
          :token="item.token" />
        <span
          v-else
          class="size-7" />
      </div>
    </template>

    <template #empty>
      <slot name="empty" />
    </template>
  </IndexTable>
</template>
